import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MapPin, Navigation, Clock, Shield } from 'lucide-react';

const Location = () => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] }
  };

  const distances = isEn ? [
    { name: 'Saadian Tombs', time: '3 min', distance: '250m', description: 'Architectural jewel of the Saadian dynasty, royal mausoleum adorned with zellige' },
    { name: 'Moulay El Yazid Mosque', time: '4 min', distance: '300m', description: 'Magnificent historic mosque in the heart of the Kasbah' },
    { name: 'Ferblantiers Square', time: '5 min', distance: '400m', description: 'Metal craftsmen, traditional lanterns and local crafts' },
    { name: 'El Badi Palace', time: '8 min', distance: '600m', description: 'Spectacular ruins of Sultan Ahmad al-Mansur\'s palace' },
    { name: 'Koutoubia', time: '10 min', distance: '900m', description: 'The iconic minaret of Marrakech, visible from all over the city' },
    { name: 'Jemaa el-Fna', time: '12 min', distance: '1km', description: 'The legendary square, beating heart of Marrakech, UNESCO World Heritage' }
  ] : [
    { name: 'Tombeaux Saadiens', time: '3 min', distance: '250m', description: 'Joyau architectural de la dynastie saadienne, mausolée royal orné de zellige' },
    { name: 'Mosquée Moulay El Yazid', time: '4 min', distance: '300m', description: 'Magnifique mosquée historique au cœur de la Kasbah' },
    { name: 'Place des Ferblantiers', time: '5 min', distance: '400m', description: 'Artisans du métal, lanternes traditionnelles et artisanat local' },
    { name: 'Palais El Badi', time: '8 min', distance: '600m', description: 'Ruines spectaculaires du palais du Sultan Ahmad al-Mansur' },
    { name: 'Koutoubia', time: '10 min', distance: '900m', description: 'Le minaret emblématique de Marrakech, visible de toute la ville' },
    { name: 'Jemaa el-Fna', time: '12 min', distance: '1km', description: 'La place légendaire, cœur battant de Marrakech, classée UNESCO' }
  ];

  const advantages = isEn ? [
    {
      icon: <Shield size={32} />,
      title: 'Secure District',
      description: 'The Kasbah is one of the quietest and safest districts in the medina. Away from tourist bustle, you enjoy authentic tranquility while remaining at the heart of history.'
    },
    {
      icon: <Navigation size={32} />,
      title: 'Guided Access',
      description: 'For your first arrival, our team welcomes you at an easy-to-access meeting point and guides you to the riad through the charming maze of alleys. By the next day, you\'ll know the way by heart.'
    },
    {
      icon: <Clock size={32} />,
      title: 'Everything on Foot',
      description: 'All major historical sites are accessible on foot in less than 15 minutes. You experience Marrakech like a local, strolling through authentic derbs, without depending on taxis.'
    }
  ] : [
    {
      icon: <Shield size={32} />,
      title: 'Quartier Sécurisé',
      description: 'La Kasbah est l\'un des quartiers les plus calmes et sûrs de la médina. Loin de l\'agitation touristique, vous profitez d\'une tranquillité authentique tout en restant au cœur de l\'histoire.'
    },
    {
      icon: <Navigation size={32} />,
      title: 'Accès Guidé',
      description: 'Pour votre première arrivée, notre équipe vous accueille à un point de rendez-vous facile d\'accès et vous guide jusqu\'au riad à travers le dédale charmant des ruelles. Dès le lendemain, vous connaîtrez le chemin par cœur.'
    },
    {
      icon: <Clock size={32} />,
      title: 'Tout à Pied',
      description: 'Tous les sites historiques majeurs sont accessibles à pied en moins de 15 minutes. Vous vivez Marrakech comme un local, en flânant dans les derbs authentiques, sans dépendre des taxis.'
    }
  ];

  return (
    <div data-testid="location-page" className="bg-[#FAF8F5] min-h-screen">
      {/* Hero */}
      <section className="pt-48 pb-32 px-8 md:px-16 lg:px-32">
        <div className="max-w-7xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[#B8860B] text-xs uppercase tracking-[0.3em] mb-6 text-center font-medium"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {isEn ? 'Where to find us' : 'Où nous trouver'}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-serif text-5xl md:text-7xl text-[#1A1A1A] mb-8 text-center"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}
          >
            {t('location.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-lg text-[#1A1A1A]/80 max-w-4xl mx-auto text-center leading-relaxed mb-12"
            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '400' }}
          >
            {isEn 
              ? 'In the heart of the Kasbah, a preserved historic district of Marrakech, Riad KAMA enjoys an exceptional location. Absolute calm in a private derb, while being just a few minutes walk from the must-see sites.'
              : 'Au cœur de la Kasbah, quartier historique préservé de Marrakech, le Riad KAMA bénéficie d\'un emplacement d\'exception. Calme absolu dans un derb privé, tout en étant à quelques minutes à pied des sites incontournables.'}
          </motion.p>

          {/* Address */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white border border-[#D4AF37]/30 p-8 md:p-12 max-w-3xl mx-auto text-center shadow-lg"
          >
            <MapPin size={40} className="text-[#B8860B] mx-auto mb-6" />
            <h3 className="font-serif text-3xl text-[#1A1A1A] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Riad KAMA
            </h3>
            <p className="text-xl text-[#1A1A1A] mb-2 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              8 Derb Riad Mechouar El Kasbah
            </p>
            <p className="text-base text-[#1A1A1A]/80 mb-6" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '400' }}>
              Kasbah, {isEn ? 'Medina of Marrakech' : 'Médina de Marrakech'}<br/>
              {isEn ? 'Morocco' : 'Maroc'}
            </p>
            <a
              href="https://www.google.com/maps?q=31.615757,-7.986259"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 gold-gradient text-[#0A0A0A] px-8 py-4 hover:shadow-2xl transition-all duration-700 uppercase tracking-[0.2em] text-xs font-semibold"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              <Navigation size={16} />
              {isEn ? 'Open in Google Maps' : 'Ouvrir dans Google Maps'}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 px-8 md:px-16 lg:px-32 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp} className="relative h-[500px] overflow-hidden border-2 border-[#D4AF37]/30 shadow-xl">
            <iframe
              src="https://www.google.com/maps?q=31.615757,-7.986259&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Riad KAMA Location"
            ></iframe>
          </motion.div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-32 px-8 md:px-16 lg:px-32 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-20">
            <h2 className="font-serif text-4xl md:text-6xl text-[#1A1A1A] mb-8" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}>
              {isEn ? 'The ideal location' : 'L\'emplacement idéal'}
            </h2>
            <p className="text-lg text-[#1A1A1A]/80 max-w-3xl mx-auto" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '400' }}>
              {isEn 
                ? 'The Kasbah offers the best of both worlds: rare tranquility in a preserved derb, while being at the center of everything.'
                : 'La Kasbah offre le meilleur des deux mondes : une tranquillité rare dans un derb préservé, tout en étant au centre de tout.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-24">
            {advantages.map((adv, index) => (
              <motion.div
                key={index}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: index * 0.2 }}
                className="bg-white p-10 border border-[#D4AF37]/30 hover:border-[#D4AF37] transition-all duration-500 shadow-lg"
              >
                <div className="text-[#B8860B] mb-6">{adv.icon}</div>
                <h3 className="font-serif text-2xl text-[#1A1A1A] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {adv.title}
                </h3>
                <p className="text-base text-[#1A1A1A]/80 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '400' }}>
                  {adv.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Distances */}
      <section className="py-32 px-8 md:px-16 lg:px-32 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-20">
            <h2 className="font-serif text-4xl md:text-6xl text-[#1A1A1A] mb-8" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}>
              {isEn ? 'Proximity to historical sites' : 'Proximité des sites historiques'}
            </h2>
            <p className="text-lg text-[#1A1A1A]/80 max-w-3xl mx-auto" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '400' }}>
              {isEn 
                ? 'All the must-see monuments of Marrakech are accessible on foot. No need for a taxi, you experience the medina from the inside.'
                : 'Tous les monuments incontournables de Marrakech sont accessibles à pied. Pas besoin de taxi, vous vivez la médina de l\'intérieur.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {distances.map((place, index) => (
              <motion.div
                key={index}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: index * 0.1 }}
                className="bg-white p-8 border border-[#D4AF37]/30 hover:border-[#B8860B] transition-all duration-500 group shadow-md"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-xl text-[#1A1A1A] font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {place.name}
                  </h3>
                  <div className="text-right">
                    <div className="text-2xl font-serif text-[#B8860B] font-medium">{place.time}</div>
                    <div className="text-xs text-[#1A1A1A]/60 font-medium">{place.distance}</div>
                  </div>
                </div>
                <p className="text-sm text-[#1A1A1A]/70 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '400' }}>
                  {place.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Location;
